import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Eye,
  RefreshCw,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import {
  Badge,
  Card,
  FieldBase,
  PrimaryButton,
  SecondaryButton,
  SelectBase,
} from "../B2CManagement/b2cUi";
import { partnerApiRequest, resolvePartnerFileUrl } from "../../../util/API";

const normalizeText = (value) => String(value ?? "").trim();

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const displayValue = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const text = normalizeText(value);
  return text || "-";
};

const guessFileKind = (fileUrl, mimeType) => {
  const url = normalizeText(fileUrl).toLowerCase();
  const type = normalizeText(mimeType).toLowerCase();
  if (type.includes("pdf") || url.endsWith(".pdf")) return "pdf";
  if (type.startsWith("image/") || /\.(png|jpg|jpeg|webp|gif|svg)$/.test(url)) {
    return "image";
  }
  return "other";
};

const statusBadgeTone = (status) => {
  const key = normalizeText(status).toLowerCase();
  if (key === "approved") return "green";
  if (key === "rejected") return "rose";
  if (key === "under_review") return "yellow";
  return "blue";
};

const SectionCard = ({ title, subtitle, children }) => (
  <Card className="p-4">
    <div>
      <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
        {title}
      </div>
      {subtitle ? <div className="mt-1 text-sm text-slate-600">{subtitle}</div> : null}
    </div>
    <div className="mt-3">{children}</div>
  </Card>
);

const FieldGrid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">{children}</div>
);

const Field = ({ label, value, mono = false }) => (
  <div>
    <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
      {label}
    </div>
    <div className={mono ? "mt-1 text-sm font-mono text-slate-800" : "mt-1 text-sm text-slate-800"}>
      {displayValue(value)}
    </div>
  </div>
);

const DocumentCard = ({ title, file }) => {
  const fileUrl = resolvePartnerFileUrl(file?.fileUrl);
  const kind = guessFileKind(fileUrl, file?.type);

  if (!fileUrl) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-sm font-bold text-slate-800">{title}</div>
        <div className="mt-1 text-xs text-slate-500">No file uploaded</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-slate-800">{title}</div>
          <div className="mt-1 text-xs text-slate-600">
            {file?.fileName ? <span className="font-semibold">{String(file.fileName)}</span> : null}
            {file?.type ? <span>{file?.fileName ? " | " : ""}{String(file.type)}</span> : null}
            {typeof file?.size === "number" ? (
              <span>{(file?.fileName || file?.type) ? " | " : ""}{Math.round(file.size / 1024)} KB</span>
            ) : null}
          </div>
        </div>

        <div className="flex gap-2">
          <a
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100"
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
          >
            Open
          </a>
          <a
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100"
            href={fileUrl}
            download
          >
            Download
          </a>
        </div>
      </div>

      <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {kind === "image" ? (
          <img src={fileUrl} alt={title} className="w-full h-auto max-h-[420px] object-contain" />
        ) : kind === "pdf" ? (
          <iframe title={title} src={fileUrl} className="w-full h-[520px]" />
        ) : (
          <div className="p-4 text-sm text-slate-600">Preview not available. Use Open or Download.</div>
        )}
      </div>
    </div>
  );
};

const PartnerApplicationManagement = () => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const [state, setState] = useState({
    loading: false,
    error: "",
    items: [],
    meta: { page: 1, limit: 50, total: 0 },
  });
  const [details, setDetails] = useState({
    open: false,
    loading: false,
    error: "",
    item: null,
  });
  const [acting, setActing] = useState({ id: "", type: "" });

  const stats = useMemo(() => {
    const list = Array.isArray(state.items) ? state.items : [];
    const submitted = list.filter((item) => normalizeText(item?.status).toLowerCase() === "submitted").length;
    const underReview = list.filter((item) => normalizeText(item?.status).toLowerCase() === "under_review").length;
    const approved = list.filter((item) => normalizeText(item?.status).toLowerCase() === "approved").length;
    return { total: list.length, submitted, underReview, approved };
  }, [state.items]);

  const fetchList = async (opts = {}) => {
    const nextPage = Number(opts.page || state.meta.page || 1);
    const limit = Number(opts.limit || state.meta.limit || 50);

    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const params = {
        page: nextPage,
        limit,
        ...(normalizeText(q) ? { q: normalizeText(q) } : {}),
        ...(normalizeText(status) ? { status: normalizeText(status) } : {}),
      };

      const res = await partnerApiRequest({
        method: "get",
        path: "/partner-applications",
        params,
      });
      const payload = res?.data || {};
      const meta = payload?.meta || {};
      setState({
        loading: false,
        error: "",
        items: Array.isArray(payload?.data) ? payload.data : [],
        meta: {
          page: Number(meta.page || nextPage),
          limit: Number(meta.limit || limit),
          total: Number(meta.total || 0),
        },
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load partner applications",
      }));
    }
  };

  const patchStatus = async (id, nextStatus, opts = {}) => {
    const appId = normalizeText(id);
    const desiredStatus = normalizeText(nextStatus).toLowerCase();
    if (!appId || !desiredStatus) return;

    setActing({ id: appId, type: "status" });
    setState((prev) => ({ ...prev, error: "" }));

    try {
      const payload = {
        status: desiredStatus,
        adminNote: normalizeText(opts.adminNote) || "Updated from internal admin panel",
      };
      if (normalizeText(opts.rejectionNote)) payload.rejectionNote = normalizeText(opts.rejectionNote);

      const res = await partnerApiRequest({
        method: "patch",
        path: `/partner-applications/${encodeURIComponent(appId)}`,
        data: payload,
      });
      const updated = res?.data?.data;

      setState((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          String(item?._id || item?.id) === appId ? (updated || item) : item
        ),
      }));

      setDetails((prev) => {
        if (!prev.open || String(prev.item?._id || prev.item?.id) !== appId) return prev;
        return { ...prev, item: updated || prev.item };
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update status",
      }));
    } finally {
      setActing({ id: "", type: "" });
    }
  };

  const requestReject = async (id) => {
    const note = window.prompt("Rejection note (visible to partner):", "");
    if (note === null) return;
    const text = normalizeText(note);
    if (!text) {
      setState((prev) => ({ ...prev, error: "Rejection note is required." }));
      return;
    }
    await patchStatus(id, "rejected", {
      rejectionNote: text,
      adminNote: "Rejected from internal admin panel",
    });
  };

  const viewDetails = async (id) => {
    const appId = normalizeText(id);
    if (!appId) return;

    setDetails({ open: true, loading: true, error: "", item: null });
    setShowRaw(false);

    try {
      const res = await partnerApiRequest({
        method: "get",
        path: `/partner-applications/${encodeURIComponent(appId)}`,
      });
      setDetails({ open: true, loading: false, error: "", item: res?.data?.data || null });
    } catch (error) {
      setDetails({
        open: true,
        loading: false,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load details",
        item: null,
      });
    }
  };

  const deleteApplication = async (id) => {
    const appId = normalizeText(id);
    if (!appId) return;

    const ok = window.confirm("Delete this application permanently?");
    if (!ok) return;

    setActing({ id: appId, type: "delete" });
    setState((prev) => ({ ...prev, error: "" }));

    try {
      await partnerApiRequest({
        method: "delete",
        path: `/partner-applications/${encodeURIComponent(appId)}`,
      });

      setState((prev) => ({
        ...prev,
        items: prev.items.filter((item) => String(item?._id || item?.id) !== appId),
      }));

      setDetails((prev) => {
        if (!prev.open || String(prev.item?._id || prev.item?.id) !== appId) return prev;
        return { open: false, loading: false, error: "", item: null };
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete application",
      }));
    } finally {
      setActing({ id: "", type: "" });
    }
  };

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminPageShell
      title="Partner Applications"
      description="Review, approve, reject, and inspect full partner application submissions."
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="gray">{stats.total} loaded</Badge>
          <Badge tone="blue">{stats.submitted} submitted</Badge>
          <Badge tone="yellow">{stats.underReview} under review</Badge>
          <Badge tone="green">{stats.approved} approved</Badge>
        </div>
      }
      actions={
        <SecondaryButton type="button" onClick={() => fetchList({ page: 1 })} disabled={state.loading}>
          <RefreshCw className="h-4 w-4" />
          {state.loading ? "Loading..." : "Refresh"}
        </SecondaryButton>
      }
    >
      <div className="space-y-6">
        <Card className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Search
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    className={`${FieldBase} pl-10`}
                    value={q}
                    onChange={(event) => setQ(event.target.value)}
                    placeholder="Tracking id, email, name, store"
                    onKeyDown={(event) => {
                      if (event.key === "Enter") fetchList({ page: 1 });
                    }}
                  />
                </div>
                <PrimaryButton type="button" onClick={() => fetchList({ page: 1 })} disabled={state.loading}>
                  Search
                </PrimaryButton>
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Status
              </label>
              <select
                className={SelectBase}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">All</option>
                <option value="submitted">submitted</option>
                <option value="under_review">under_review</option>
                <option value="approved">approved</option>
                <option value="rejected">rejected</option>
                <option value="resubmitted">resubmitted</option>
              </select>
            </div>
          </div>

          {state.error ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">
              {state.error}
            </div>
          ) : null}
        </Card>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Tracking
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Store
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Applicant
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Email
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Submitted
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.items.length ? (
                  state.items.map((item) => {
                    const id = String(item?._id || item?.id || "");
                    const busy = acting.id === id;
                    return (
                      <tr key={id} className="border-b border-slate-200 last:border-b-0">
                        <td className="px-4 py-3 font-mono text-slate-700">{item.applicationNumber || "-"}</td>
                        <td className="px-4 py-3 text-slate-700">{item.storeName || "-"}</td>
                        <td className="px-4 py-3 text-slate-700">{item.applicantFullName || "-"}</td>
                        <td className="px-4 py-3 text-slate-700">{item.emailAddress || "-"}</td>
                        <td className="px-4 py-3">
                          <Badge tone={statusBadgeTone(item.status)}>{displayValue(item.status)}</Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{formatDate(item.createdAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            <SecondaryButton type="button" onClick={() => viewDetails(id)}>
                              <Eye className="h-4 w-4" />
                              View
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              onClick={() => patchStatus(id, "approved")}
                              disabled={busy}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Approve
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              onClick={() => requestReject(id)}
                              disabled={busy}
                            >
                              <XCircle className="h-4 w-4" />
                              Reject
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              onClick={() => deleteApplication(id)}
                              disabled={busy}
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </SecondaryButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-500" colSpan={7}>
                      {state.loading ? "Loading..." : "No applications found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-white">
            <div className="text-xs font-semibold text-slate-600">
              Page {state.meta.page} | Showing {state.items.length} | Total {state.meta.total}
            </div>
            <div className="flex gap-2">
              <SecondaryButton
                type="button"
                onClick={() => fetchList({ page: Math.max(1, state.meta.page - 1) })}
                disabled={state.loading || state.meta.page <= 1}
              >
                Prev
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() => fetchList({ page: state.meta.page + 1 })}
                disabled={state.loading || state.items.length < state.meta.limit}
              >
                Next
              </SecondaryButton>
            </div>
          </div>
        </Card>

        {details.open ? (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setDetails({ open: false, loading: false, error: "", item: null })}
            />

            <div
              className="relative w-full max-w-4xl max-h-[90vh] bg-white shadow-2xl border border-slate-200 rounded-2xl overflow-hidden flex flex-col"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Application</div>
                  <div className="mt-1 text-lg font-black text-slate-900">Details</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {details.item?._id || details.item?.id ? (
                    <>
                      <SecondaryButton
                        type="button"
                        onClick={() => patchStatus(String(details.item?._id || details.item?.id), "approved")}
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Approve
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => requestReject(String(details.item?._id || details.item?.id))}
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => deleteApplication(String(details.item?._id || details.item?.id))}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </SecondaryButton>
                    </>
                  ) : null}

                  <SecondaryButton
                    type="button"
                    onClick={() => setDetails({ open: false, loading: false, error: "", item: null })}
                  >
                    Close
                  </SecondaryButton>
                </div>
              </div>

              <div className="p-5 overflow-auto flex-1">
                {details.loading ? <div className="text-sm text-slate-600">Loading...</div> : null}
                {details.error ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">
                    {details.error}
                  </div>
                ) : null}
                {details.item ? (
                  <>
                    <SectionCard title="Summary">
                      <FieldGrid>
                        <Field label="Tracking ID" value={details.item.applicationNumber} mono />
                        <Field label="Status" value={details.item.status} />
                        <Field label="Submitted" value={formatDate(details.item.createdAt)} />
                        <Field label="Last Updated" value={formatDate(details.item.updatedAt)} />
                      </FieldGrid>
                    </SectionCard>

                    <div className="mt-4 space-y-4">
                      <SectionCard title="Applicant" subtitle="Identity and contact">
                        <FieldGrid>
                          <Field label="Full Name" value={details.item.applicantFullName} />
                          <Field label="Designation" value={details.item.designation} />
                          <Field label="Email" value={details.item.emailAddress} />
                          <Field label="Mobile" value={details.item.mobileNumber} />
                        </FieldGrid>
                      </SectionCard>

                      <SectionCard title="Business" subtitle="Business identity">
                        <FieldGrid>
                          <Field label="Business Type" value={details.item.businessType} />
                          <Field label="Legal Business Name" value={details.item.legalBusinessName} />
                          <Field label="BIN/TIN" value={details.item.binTin} />
                          <Field label="Country" value={details.item.registeredCountry} />
                          <Field label="City/Municipality" value={details.item.registeredCityMunicipality} />
                          <Field label="District" value={details.item.registeredDistrict} />
                          <Field label="Postal Code" value={details.item.registeredPostalCode} />
                          <Field label="Address Line 1" value={details.item.registeredAddressLine1} />
                          <Field label="Address Line 2" value={details.item.registeredAddressLine2} />
                        </FieldGrid>
                      </SectionCard>

                      <SectionCard title="Store" subtitle="Store information">
                        <FieldGrid>
                          <Field label="Store Name" value={details.item.storeName} />
                          <Field label="Category" value={details.item.storeCategory} />
                          <Field label="Other Category" value={details.item.storeCategoryOther} />
                          <Field label="Website/Social" value={details.item.websiteOrSocial} />
                          <Field label="Pickup/Delivery Address" value={details.item.deliveryPickupAddress} />
                          <Field label="Short Description" value={details.item.shortStoreDescription} />
                        </FieldGrid>
                      </SectionCard>

                      <SectionCard title="Documents" subtitle="Uploaded documents">
                        <div className="space-y-4">
                          <DocumentCard title="Trade License" file={details.item.tradeLicenseFile} />
                          <DocumentCard title="Store Logo" file={details.item.storeLogoFile} />
                        </div>
                      </SectionCard>

                      <SectionCard title="System" subtitle="Internal identifiers">
                        <FieldGrid>
                          <Field label="Mongo ID" value={details.item._id || details.item.id} mono />
                          <Field label="User ID" value={details.item.userId} mono />
                          <Field label="Admin Note" value={details.item.adminNote} />
                          <Field label="Reviewed At" value={formatDate(details.item.reviewedAt)} />
                        </FieldGrid>
                      </SectionCard>
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white">
                      <button
                        type="button"
                        className="w-full px-4 py-3 flex items-center justify-between text-sm font-bold text-slate-800 hover:bg-slate-50 rounded-2xl"
                        onClick={() => setShowRaw((value) => !value)}
                      >
                        Raw JSON
                        <span className="text-slate-400">{showRaw ? "-" : "+"}</span>
                      </button>
                      {showRaw ? (
                        <pre className="px-4 pb-4 whitespace-pre-wrap break-words text-xs text-slate-800">
                          {JSON.stringify(details.item, null, 2)}
                        </pre>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AdminPageShell>
  );
};

export default PartnerApplicationManagement;
